import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import gsap from 'gsap';
import Lines from '../../assets/svgs/lines.svg';
import LinesMobile from '../../assets/svgs/lines-mobile.svg';
import Slider from '../../assets/svgs/slider.svg';
import SliderMobile from '../../assets/svgs/slider-mobile.svg';
import { media } from '../../utils/media-queries';
import { useWindowSize } from '../../hooks/useWindowSize';
import { lerp } from '../../utils/helper';

const History = ({ timeline }) => {
  const ADDED_DRAG_SPEED = 1.8;
  const EASE = 0.825;
  const END_MARGIN = 200;

  const rootRef = useRef(null);
  const sliderRef = useRef(null);
  const handlerWrapperRef = useRef(null);

  const targetRef = useRef(0);
  const currentRef = useRef(0);
  const handlerRef = useRef(null);

  const [isMobile, setIsMobile] = useState(false);
  const [isSmall, setIsSmall] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState();
  const [dragBefore, setDragBefore] = useState();

  const [sectionWidth, setSectionWidth] = useState();
  const [handlerWidth, setHandlerWidth] = useState();
  const [handlerWrapperWidth, setHandlerWrapperWidth] = useState();
  const [handlerWrapperLeft, setHandlerWrapperLeft] = useState();
  const [containerWidth, setContainerWidth] = useState();
  const [end, setEnd] = useState();
  const [cache, setCache] = useState({});
  const [animImgs, setAnimImgs] = useState([]);

  const size = useWindowSize();

  useEffect(() => {
    setIsMobile(size.width < 1025);
    setIsSmall(size.width < 768);

    // const images = document.querySelectorAll('.anim-img');
    // setAnimImgs([...images]);
  }, [size]);

  const handleUp = () => setIsDragging(false);

  const handleDown = (e) => {
    setIsDragging(true);

    const x = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    setDragStart(x);
    setDragBefore(targetRef.current);
  };

  const handleMove = (e) => {
    if (isDragging) {
      const x = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;

      targetRef.current =
        dragBefore +
        (dragStart - x) * (isMobile ? ADDED_DRAG_SPEED * 2 : ADDED_DRAG_SPEED);

      targetRef.current = gsap.utils.clamp(0, (containerWidth - sectionWidth), targetRef.current);
    }
  };

  const handleWheel = (e) => {
    targetRef.current += e.deltaX;
    targetRef.current = gsap.utils.clamp(0, (containerWidth - sectionWidth), targetRef.current);
  }

  const getCache = () => {
    const { left, right, width } = sliderRef.current.getBoundingClientRect();

    setCache({
      left,
      width,
      end: right,
      extra: 0,
    });
  };

  const render = () => {

    const clampedX = gsap.utils.clamp(
      0,
      containerWidth - sectionWidth,
      currentRef.current
    );

    const reversed = clampedX * -1;

    gsap.set(sliderRef.current, {
      x: reversed,
    });

    gsap.to(handlerRef.current, {
      x: (handlerWrapperWidth - handlerWidth) * (clampedX / (containerWidth - sectionWidth)),
    });

  };

  const run = () => {
    currentRef.current = lerp(targetRef.current, currentRef.current, EASE);
    targetRef.current = gsap.utils.clamp(0, end, targetRef.current);

    // animImgs.forEach((img) => {
    //   img.style.transform = `translateX(${(currentRef.current - targetRef.current) / 10
    //     }px) scale(1.3)`;
    // });

    render();
  };

  /**
   * RUN
   */
  useEffect(() => {
    setSectionWidth(rootRef.current.clientWidth);
    setContainerWidth(sliderRef.current.clientWidth);
    setHandlerWidth(handlerRef.current.clientWidth);
    setHandlerWrapperWidth(handlerWrapperRef.current.clientWidth);
    setHandlerWrapperLeft(
      handlerWrapperRef.current.getBoundingClientRect().left
    );
    setEnd(containerWidth - sectionWidth + END_MARGIN * 2);

    getCache();
    gsap.ticker.add(run);

    return () => {
      gsap.ticker.remove(run);
    };
  }, [containerWidth, sectionWidth, END_MARGIN, end]);

  const handleHandlerDown = (e) => {
    document.querySelector('body').style.overflowX = 'hidden';
    setIsDragging(true);

    const x = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;

    setDragStart(x);
    setDragBefore(targetRef.current);
  };

  const handleHandlerLeave = () => {
    setIsDragging(false);
  };

  const handleHandlerUp = () => {
    document.querySelector('body').style.overflowX = 'visible';
    setIsDragging(false);
  };

  const handleHandlerMove = (e) => {
    if (!isDragging) return;

    let x = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    x = isMobile
      ? x - handlerWrapperLeft - handlerWidth
      : x - handlerWrapperLeft - handlerWidth / 2;

    const calcWidthDiff = end / (handlerWrapperWidth - handlerWidth / 2);

    if (x > 0) targetRef.current = x * calcWidthDiff;
    if (x < 0) {
      targetRef.current = 0;
      x = 0;
    }
    if (x > handlerWrapperWidth - handlerWrapperLeft)
      x = handlerWrapperWidth - handlerWidth;

    // gsap.to(handlerRef.current, {
    //   x,
    // });
  }


  return (
    <>
      <StyledSlider
        className="scroll-section"
        ref={rootRef}
        onMouseUp={handleUp}
        onMouseDown={handleDown}
        onMouseMove={handleMove}
        onTouchEnd={handleUp}
        onTouchStart={handleDown}
        onTouchMove={handleMove}
        onWheel={handleWheel}
      >
        <StyledHistory>
          <div className="container slide" ref={sliderRef}>
            {timeline.map((item, i) => (
              <div className="year-wrapper" key={i}>
                <p className="year">{item.year}</p>
                <div className="images">
                  {item.content.map((contentItem, contentI) => (
                    <figure
                      key={contentI}
                      className={contentI == 0 ? 'left' : 'bottom'}
                    >
                      <div className="img-wrapper">
                        <GatsbyImage
                          className="anim-img"
                          image={contentItem.image?.asset.gatsbyImageData}
                          alt={contentItem.alt}
                        />
                      </div>
                      <figcaption>
                        <p className="month">{contentItem.month}</p>
                        <p className="caption">{contentItem.caption}</p>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </StyledHistory>
      </StyledSlider>
      <StyledHandle
        className="handle"
        onMouseDown={handleHandlerDown}
        onMouseLeave={handleHandlerLeave}
        onMouseUp={handleHandlerUp}
        onMouseMove={handleHandlerMove}
        onTouchEnd={handleHandlerUp}
        onTouchStart={handleHandlerDown}
        onTouchMove={handleHandlerMove}
        ref={handlerWrapperRef}
      >
        {isSmall ? <LinesMobile /> : <Lines />}
        <div className="handler" ref={handlerRef}>
          {isMobile ? <SliderMobile /> : <Slider />}
        </div>
      </StyledHandle>
    </>
  );
};

const StyledSlider = styled.section`
  // overflow: hidden;
  

  .container {
    display: flex;
  }

  @media ${media.m} {

    grid-column: 1 / 12;
  }

  @media ${media.L} {
    margin-right:0;
    grid-column: 2 / 12;
  }
`;

const StyledHistory = styled.div`
  color: var(--red);
  display: flex;
  position: relative;
  // overflow: hidden;
  width: calc(100vw - (var(--sp-1-8) * 2));
  cursor: ew-resize;

  .year-wrapper {
    display: flex;
    flex-direction: column;
    

    &:first-child {
      padding-left:0;
    }
  }

  * {
    pointer-events: none;
  }

  .year {
    font-size: 12rem;
    font-weight: 300;
    padding-right: var(--sp-5);
  }

  .images {
    display: flex;
  }

  figure {
    min-width: 75vw;
    padding-right: var(--sp-1-3);

    .img-wrapper {
      overflow: hidden;
      width:100%;
      height:0;
      padding-bottom:66.86%;
      position:relative;

      .gatsby-image-wrapper {
        position:absolute;
        top:0;
        left:0;
        width:100%;
        height:100%;
        object-fit:cover;
        object-position:center;
      }
    }

    // .gatsby-image-wrapper {
    //   height: calc(75vw * 0.65);
    //   min-width: 100%;
    // }

    figcaption {
      padding-top: 1.6rem;
      max-width:25rem;
      font-size: var(--fs-small);
      width: 100%;
      display: block;

      .month {
        font-weight: 600;
      }
    }
  }

  @media ${media.L} {
    height: auto;
    width: calc(100vw - var(--sp-1-8));
    margin-top: var(--sp-5);

    .year-wrapper {
      flex-direction: row;
    }

    .year {
      // padding-left: var(--sp-8);
      font-size: 18rem;
      transform: translateY(calc(var(--sp-5) * -1));
    }

    figure {
      min-width: 42vw;
      height: auto;
      position: relative;

      .gatsby-image-wrapper {
        height: auto;
      }

      // figcaption {
      //   width: 15vw;
      // }

      &.left {
        figcaption {
          position: absolute;
          bottom: 0;
          transform: translateX(calc(-100% - 3rem));
        }
      }

      &.bottom,
      &.top {
        min-width: 22vw;
        // height: 12.5vw;
      }

      &.bottom {
        figcaption {
          // padding-left: var(--sp-8);
          width: 100%;
        }
      }

      &.top {
        margin-top: 12.5vw;

        figcaption {
          position: absolute;
          top: 0;
          transform: translateY(-10rem);
        }
      }
    }
  }
`;

const StyledHandle = styled.div`
  height: 32px;
  margin-top: 2rem;
  z-index: 10;
  position: relative;
  // padding-bottom: var(--sp-8);
  cursor: pointer;

  > svg {
    width: 100%;

    path {

      &:first-child,
      &:last-child {
        transform:scaleY(0.5);
        transform-origin:center;
      }
    }
  }

  .handler {
    pointer-events: none;
    position: absolute;
    top: 0;
    transform-origin: left left;
  }

  @media ${media.L} {
    grid-column: 2 / 12;
    // padding-bottom: var(--sp-13);
  }
`;

History.propTypes = {
  timeline: PropTypes.array,
};

export default History;
